/* eslint-disable react/display-name */
import React from 'react';
import classNames from 'classnames';
import ReactTable from 'react-table';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/customTableStyle';
import PaginationComponent from 'components/CustomTable/Pagination';
import { getAccessibilityLabels, getCommonLabels } from 'redux/modules/dynamicLabels/selectors';
import { useSelector } from 'react-redux';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ICheckboxCellProps {
	selected: number[];
	index: number;
	onSelect: (e: number[]) => void;
	classes: {
		checkbox: string;
		checkboxChecked: string;
	};
	disable?: boolean;
	ariaLabel?: string;
}

const CheckboxCell = ({ selected, index, onSelect, classes, disable, ariaLabel = '' }: ICheckboxCellProps) => {
	const isChecked = selected.includes(index);
	return (
		<CustomCheckbox
			disableLabel
			className={classNames(classes.checkbox, {
				[classes.checkboxChecked]: isChecked,
			})}
			checked={isChecked}
			onClick={() =>
				isChecked ? onSelect([...selected].filter((ind) => index !== ind)) : onSelect([...selected, index])
			}
			disabled={disable}
			checkboxProps={{ inputProps: { 'aria-label': ariaLabel } }}
		/>
	);
};

interface IDownloadMenuItem {
	name: string;
	onClick: VoidFunction;
}

export interface ISelectPanel {
	confirmationNotRequired?: boolean;
	confirmationDialogTitle?: string;
	confirmationDialogBody?: string | React.ReactElement;
	confirmationDialogButtonDeleteHidden?: boolean;
	selectedElements?: number;
	downloadMenuItems?: IDownloadMenuItem[];
	deleteItems?: () => void;
	deleteItemsAction?: string;
	editItems?: () => void;
	disableDelete?: boolean;
	disableDeleteText?: string;
	customItems?: React.ReactElement;
}

interface ITableProps {
	tableProps: {
		getTrProps?: (state: any, row: any, ...rest: any) => void;
		columns: any[];
		data: any;
		defaultPageSize?: number;
		pageSize?: number;
		pageSizeOptions?: number[];
		loading?: boolean;
		SubComponent?: any;
		expanded?: any;
		onExpandedChange?: any;
		stroke?: boolean;
	};
	total?: number;
	alignColumnsRight?: number[];
	search?: string | React.ReactElement;
	withHeader?: boolean;
	withCheckbox?: boolean;
	onSelect?: (e: any) => void;
	selected?: any[];
	withFooter?: boolean;
	showPaginationTop?: boolean;
	additionalNote?: string;
	onPaggingChange?: any;
	onPageSizeChange?: any;
	selectPanel?: ISelectPanel;
	showPaginationBottom?: boolean; // pass this prop to show pagination on the bottom of the page
	alignColumnsCenter?: number[]; // this prop is used to align the columns mentioned in array to be center aligned rather than right alignment.
	pageSize?: number;
	pages?: number;
	page?: number;
	enabledCheckoxes?: any[]; //this prop contains the index od enabled checkboxes, so we can customize it based on requirement
}

const CustomTable = (props: ITableProps) => {
	const commonLabels = useSelector(getCommonLabels);
	const {
		tableProps,
		total,
		alignColumnsRight,
		search,
		withHeader,
		withCheckbox,
		withFooter,
		selected,
		onSelect,
		selectPanel,
		additionalNote,
		showPaginationTop = true,
		onPaggingChange,
		onPageSizeChange,
		showPaginationBottom = true,
		alignColumnsCenter,
		pageSize,
		pages,
		page,
		enabledCheckoxes,
	} = props;

	const { getTrProps = () => ({}) } = tableProps;

	const classes = useStyles({ alignColumnsCenter, alignColumnsRight });
	const accessibilityLabels = useSelector(getAccessibilityLabels);

	const newGetTrProps = React.useCallback(
		(state, row, ...props) => {
			const trPropsValue = getTrProps(state, row, ...props);
			if (row?.original?.new) {
				// @ts-ignore
				return { ...trPropsValue, className: classNames(trPropsValue.className, classes.newRow) };
			} else {
				return trPropsValue;
			}
		},
		[getTrProps, classes.newRow]
	);

	function getAllIndices(length: number) {
		return Array.from(Array(length).keys());
	}

	const columns = React.useMemo(() => {
		const userColumns = tableProps.columns || [];
		const data = tableProps.data || [];
		const disableCheckbox = (index: any) => {
			//if enabled checbox doesn't have index of a particular check box then we disable it
			if (enabledCheckoxes) {
				if (enabledCheckoxes?.indexOf(index) >= -1) return false;
			}
		};
		if (withCheckbox && selected && !!onSelect) {
			const isChecked = data.length > 0 && data.length === selected.length;
			const checkbox = [
				{
					width: 48,
					filterable: false,
					sortable: false,
					resizable: false,
					Header: (
						<CustomCheckbox
							className={classNames(
								classes.checkbox,
								classes.checkboxAll,
								{
									[classes.checkboxChecked]: data.length > 0 && selected.length > 0,
								},
								isChecked ? 'checkedRow' : ''
							)}
							checked={data.length > 0 && data.length === selected.length}
							indeterminate={
								data.length !== 0 && selected.length !== 0 && selected.length !== data.length
							}
							onClick={() => {
								if (selected.length === data.length || selected.length === enabledCheckoxes?.length) {
									onSelect([]);
								} else {
									onSelect(getAllIndices(data.length));
								}
							}}
							disableLabel
							checkboxProps={{ inputProps: { 'aria-label': accessibilityLabels?.selectAllRows } }}
						/>
					),
					Cell: ({ index }: { index: number }) => {
						return (
							<CheckboxCell
								selected={selected}
								classes={classes}
								index={index}
								onSelect={onSelect}
								disable={disableCheckbox(index)}
								ariaLabel={accessibilityLabels?.selectRow}
							/>
						);
					},
				},
			];
			return [...checkbox, ...userColumns];
		}
		return userColumns;
	}, [withCheckbox, tableProps.columns, tableProps.data, classes, onSelect, selected]);
	return (
		// @ts-ignore
		<ReactTable
			onPageChange={onPaggingChange}
			onPageSizeChange={onPageSizeChange}
			className={classNames(classes.table, {
				[classes.alignColumnsRight]: alignColumnsRight,
				[classes.borderRadius]: !withFooter,
				[classes.withStroke]: tableProps.stroke,
				[classes.alignColumnsCenter]: alignColumnsCenter,
			})}
			showPaginationTop={showPaginationTop}
			PaginationComponent={(props: any) => (
				<PaginationComponent
					{...props}
					total={total}
					search={search}
					withHeader={withHeader}
					selectPanel={selectPanel}
					canNext={pageSize && (page || page == 0) && pages ? page + 1 < pages : props.canNext}
					canPrevious={pageSize && (page || page == 0) && pages ? page + 1 > 1 : props.canPrevious}
					pageSize={pageSize ? pageSize : props.pageSize}
					pages={pages ? pages : props.pages}
					page={page ? page : props.page}
					additionalNote={additionalNote}
				/>
			)}
			{...tableProps}
			getTrProps={newGetTrProps}
			showPaginationBottom={showPaginationBottom}
			columns={columns}
			noDataText={commonLabels.noRowsFound}
			//add default sort method to override the sorting function of react table for our customization
			defaultSortMethod={(a: any, b: any) => {
				if ((a === null || a === undefined) && (b === null || b === undefined)) {
					return 0;
				}

				if (a === null || a === undefined) {
					return 1;
				}

				if (b === null || b === undefined) {
					return -1;
				}

				return a.toString().localeCompare(b.toString());
			}}
		/>
	);
};

export default CustomTable;
